import type { EChartsOption } from 'echarts';
import { tableFromHeadersRows, type TableModel } from './table-model';

type ApiLocale = 'pt' | 'en';

function parseLooseNumber(value: string): number | null {
  const trimmed = value.trim();
  if (!trimmed) return null;

  if (/^-?\d+(\.\d+)?$/.test(trimmed)) return Number(trimmed);
  if (/^-?\d+,\d+$/.test(trimmed)) return Number(trimmed.replace(',', '.'));
  if (/^-?\d{1,3}(\.\d{3})*,\d+$/.test(trimmed)) {
    return Number(trimmed.replace(/\./g, '').replace(',', '.'));
  }

  const parsed = Number(trimmed);
  return Number.isFinite(parsed) ? parsed : null;
}

/** Arredonda valores numéricos para exibição na tabela — o export mantém a precisão original. */
function formatTableCellDisplay(value: string, locale: ApiLocale): string {
  const num = parseLooseNumber(value);
  if (num === null) return value;

  const intlLocale = locale === 'en' ? 'en-US' : 'pt-BR';
  const isNearInteger = Math.abs(num - Math.round(num)) < 1e-6;

  if (isNearInteger) {
    return new Intl.NumberFormat(intlLocale, { maximumFractionDigits: 0 }).format(
      Math.round(num),
    );
  }

  const rounded = Math.round(num * 100) / 100;

  return new Intl.NumberFormat(intlLocale, {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format(rounded);
}

/** Converte células ECharts (número, string ou `{ value }`) em texto CSV/tabela. */
function serializeCellValue(value: unknown): string {
  if (value == null) return '';
  if (typeof value === 'number' || typeof value === 'boolean') return String(value);
  if (typeof value === 'string') return value;
  if (Array.isArray(value)) {
    return value.map(serializeCellValue).filter((part) => part.length > 0).join(', ');
  }
  if (typeof value === 'object') {
    const obj = value as Record<string, unknown>;
    if ('value' in obj && obj['value'] !== undefined) {
      return serializeCellValue(obj['value']);
    }
    if (typeof obj['name'] === 'string' && !('value' in obj)) {
      return obj['name'];
    }
  }
  return '';
}

function isTupleSeriesData(data: unknown[] | undefined): boolean {
  const first = data?.[0];
  if (Array.isArray(first) && first.length >= 2) return true;
  if (typeof first === 'object' && first !== null) {
    const value = (first as { value?: unknown }).value;
    return Array.isArray(value) && value.length >= 2;
  }
  return false;
}

function extractTupleXY(point: unknown): { x: string; y: string } {
  if (Array.isArray(point) && point.length >= 2) {
    return { x: serializeCellValue(point[0]), y: serializeCellValue(point[1]) };
  }
  if (typeof point === 'object' && point !== null) {
    const value = (point as { value?: unknown }).value;
    if (Array.isArray(value) && value.length >= 2) {
      return { x: serializeCellValue(value[0]), y: serializeCellValue(value[1]) };
    }
    return { x: '', y: serializeCellValue(value ?? point) };
  }
  return { x: '', y: serializeCellValue(point) };
}

type CsvSeriesItem = { name?: string; data?: unknown[] };

function appendCategoryAxisRows(
  fileData: string,
  opt: EChartsOption,
  series: CsvSeriesItem[],
  locale: ApiLocale,
): string {
  const xData = (opt.xAxis as { data?: string[] })?.data ?? [];
  if (!xData.length) return fileData;

  let result = fileData;
  result += locale === 'pt' ? 'EixoX;' : 'X-Axis;';
  result += series.map((m) => m.name).join(';') + '\r\n';

  for (let i = 0; i < xData.length; i++) {
    result +=
      `${xData[i].toString().replace(/\n/g, ' ')};` +
      series
        .map((m) => serializeCellValue((m as { data?: unknown[] }).data?.[i]))
        .join(';') +
      '\r\n';
  }
  return result;
}

function appendLineRows(
  fileData: string,
  series: CsvSeriesItem[],
  locale: ApiLocale,
): string {
  const data = (series[0] as { data?: unknown[] })?.data;
  const countLines = data?.length;
  if (!countLines) return fileData;

  let result = fileData;

  if (isTupleSeriesData(data)) {
    result += locale === 'pt' ? 'EixoX;' : 'X-Axis;';
    result += series.map((s) => s.name).join(';') + '\r\n';
    for (let indexLine = 0; indexLine < countLines; indexLine++) {
      const first = extractTupleXY(data![indexLine]);
      result += `${first.x};`;
      for (const serie of series) {
        const point = (serie as { data?: unknown[] }).data?.[indexLine];
        result += `${extractTupleXY(point).y};`;
      }
      result += '\r\n';
    }
    return result;
  }

  for (const serie of series) {
    result += `${serie.name};`;
  }
  result += '\r\n';

  for (let indexLine = 0; indexLine < countLines; indexLine++) {
    for (const serie of series) {
      const point = (serie as { data?: unknown[] }).data?.[indexLine];
      result += `${serializeCellValue(point)};`;
    }
    result += '\r\n';
  }
  return result;
}

function scatterRowCsv(item: unknown): string {
  const values = Array.isArray(item)
    ? item
    : (item as { value?: unknown })?.value;
  if (!Array.isArray(values)) {
    return serializeCellValue(item);
  }
  return values.map((cell) => serializeCellValue(cell)).join(';');
}

/**
 * Gera CSV a partir de opções ECharts — portado do `ChartUtil.generateCsv` do front-17.
 */
function generateEchartsCsv(
  chart: EChartsOption,
  locale: ApiLocale,
): string | null {
  try {
    let opt = { ...chart } as EChartsOption & Record<string, unknown>;
    if (!Array.isArray(opt.xAxis) && (opt.xAxis as { type?: string })?.type === 'value') {
      opt = { ...chart, xAxis: chart.yAxis, yAxis: chart.xAxis } as EChartsOption & Record<string, unknown>;
    }

    if (!Array.isArray(opt.series) && opt.series) {
      opt.series = [opt.series];
    }
    const series = (opt.series ?? []).filter((item) => item.name !== 'aux');
    opt.series = series;

    let fileData = '';

    if (series[0]?.type === 'sankey') {
      fileData += locale === 'pt' ? 'DE;PARA;VALOR\r\n' : 'OF;TO;VALUE\r\n';
      const links = (series[0] as { links?: { source: string; target: string; value: number }[] }).links;
      if (links) {
        for (const v of links) {
          fileData += `${v.source};${v.target};${v.value}\r\n`;
        }
      }
    } else if (series[0]?.type === 'treemap') {
      fileData += locale === 'pt' ? 'NOME;VALOR\r\n' : 'NAME;VALUE\r\n';
      for (const v of series[0].data as { name: string; value: number }[]) {
        fileData += `${v.name};${v.value}\r\n`;
      }
    } else if (series[0]?.type === 'scatter') {
      fileData +=
        locale === 'pt'
          ? 'EIXOX;EIXOY;RAIO;NOME\r\n'
          : 'X-AXIS;Y-AXIS;RADIUS;NAME\r\n';
      for (const item of (series[0].data ?? []) as unknown[]) {
        fileData += `${scatterRowCsv(item)}\r\n`;
      }
    } else if (series[0]?.type === 'map') {
      fileData += locale === 'pt' ? 'Nome;Valor\r\n' : 'Name;Value\r\n';
      for (const item of (series[0].data ?? []) as { name?: string; value?: unknown }[]) {
        fileData += `${item.name ?? ''};${serializeCellValue(item.value)}\r\n`;
      }
    }

    const xData =
      opt.xAxis && !Array.isArray(opt.xAxis)
        ? ((opt.xAxis as { data?: string[] }).data ?? [])
        : [];

    if (!fileData && xData.length > 0) {
      fileData = appendCategoryAxisRows(fileData, opt, series as CsvSeriesItem[], locale);
    }

    if (!fileData && series[0]?.type === 'line') {
      fileData = appendLineRows(fileData, series as CsvSeriesItem[], locale);
    } else if (!fileData && series[0]?.type === 'bar') {
      if (opt.xAxis) {
        fileData += ';';
      }
      for (const serie of series) {
        fileData += `${serie.name};`;
      }
      fileData += '\r\n';

      const countLines = (series[0] as { data?: unknown[] }).data?.length;
      if (countLines) {
        for (let indexLine = 0; indexLine < countLines; indexLine++) {
          if (opt.xAxis) {
            const xAxisData = Array.isArray(opt.xAxis)
              ? (opt.xAxis[0] as { data?: string[] })?.data
              : (opt.xAxis as { data?: string[] })?.data;
            fileData += `${xAxisData?.[indexLine] ?? ''};`;
          }
          for (let indexColumn = 0; indexColumn < series.length; indexColumn++) {
            const elementColumn = series[indexColumn] as { data?: unknown[] };
            fileData += `${serializeCellValue(elementColumn.data?.[indexLine])};`;
          }
          fileData += '\r\n';
        }
      }
    } else if (!fileData) {
      fileData += locale === 'pt' ? 'Nome;' : 'Name;';
      if (series[0]) {
        fileData += series[0].name + '\r\n';
        for (const v of (series[0].data ?? []) as { name: string; value: number }[]) {
          fileData += `${v.name};${v.value}\r\n`;
        }
      }
    }

    return fileData || null;
  } catch (e) {
    console.error(e);
    return null;
  }
}

/**
 * Modelo de tabela a partir das options do ECharts.
 *
 * As linhas guardam o valor **cru** (é o que CSV/XLSX devem levar) e o arredondamento
 * para leitura fica em `format`, aplicado só na renderização.
 */
export function echartsOptionsToTableModel(
  chart: EChartsOption,
  locale: ApiLocale,
): TableModel | null {
  const csv = generateEchartsCsv(chart, locale);
  if (!csv) return null;
  const lines = csv.split(/\r?\n/).filter((line) => line.length > 0);
  if (!lines.length) return null;

  const model = tableFromHeadersRows(
    lines[0].split(';'),
    lines.slice(1).map((line) => line.split(';')),
  );

  for (const column of model.columns) {
    if (!column.numeric) continue;
    column.format = (value) => formatTableCellDisplay(String(value ?? ''), locale);
  }

  return model;
}
